import React, { useState, useContext, useEffect, useCallback } from "react";
import { buchApi } from "../api/buchApi";
import { BuchDTO, AutorDTO, ExemplarDTO } from "../types/buchTypes";
import "../css/BuchVerwaltung.css";
import { SelectedBookContext } from "../context/SelectedBookContext";

const emptyForm: BuchDTO = {
  titel: "",
  isbn: "",
  verlag: "",
  jahr: new Date().getFullYear(),
  sprache: "",
  medienart: "",
  kategorieName: "",
  exemplare: [],
  autoren: [],
  id: 0,
  bildUrl: "", // Wichtig: Damit das Feld initialisiert ist
};

const BuchVerwaltung = () => {
  const { bookId, setBookId } = useContext(SelectedBookContext);

  // View-Steuerung: 'list' oder 'form'
  const [view, setView] = useState<"list" | "form">(bookId ? "form" : "list");

  // Daten für die Liste
  const [allBooks, setAllBooks] = useState<BuchDTO[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Daten für das Formular
  const [form, setForm] = useState<BuchDTO>(emptyForm);
  const [autor, setAutor] = useState<AutorDTO>({ vorname: "", nachname: "" });
  const [availableAutoren, setAvailableAutoren] = useState<AutorDTO[]>([]);
  const [selectedAutorId, setSelectedAutorId] = useState<number | null>(null);
  const [exemplar, setExemplar] = useState<ExemplarDTO>({
    inventarnummer: "",
    status: "VERFUEGBAR",
    standort: "",
  });

  // --- 1. INITIALISIERUNG & LISTE LADEN ---

  useEffect(() => {
    const loadAutoren = async () => {
      try {
        const aut = await buchApi.getAllAutors();
        setAvailableAutoren(aut);
      } catch (e) {
        console.error("Fehler beim Laden der Autoren", e);
      }
    };
    loadAutoren();
  }, []);

  // loadBooks mit useCallback geschützt
  const loadBooks = useCallback(async () => {
    try {
      const books = await buchApi.getAllBuecher();
      setAllBooks(books);
    } catch (e) {
      console.error("Fehler beim Laden der Bücher", e);
    }
  }, []);

  const loadBookDetails = useCallback(async (id: number) => {
    try {
      const fullBook = await buchApi.getBuchById(id);
      setForm(fullBook);
    } catch (e) {
      console.error(e);
      alert("Buch konnte nicht geladen werden.");
      setView("list");
    }
  }, []);

  // Wenn bookId im Context gesetzt ist (z.B. durch Klick in der Buchsuche), lade Details
  useEffect(() => {
    if (bookId) {
      loadBookDetails(bookId);
      setView("form");
    }
  }, [bookId, loadBookDetails]);

  // Wenn kein Buch ausgewählt ist, lade die Liste
  useEffect(() => {
    if (!bookId) {
      loadBooks();
    }
  }, [bookId, loadBooks]); // bookId und loadBooks eingefügt

  // --- 2. LIST VIEW LOGIK ---

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const filteredBooks = allBooks.filter(
    (b) =>
      b.titel.toLowerCase().includes(searchTerm) || b.isbn.includes(searchTerm),
  );

  const handleCreateNewClick = () => {
    setBookId(null); // Context leeren
    setForm(emptyForm); // Formular leeren
    setView("form"); // Ansicht wechseln
  };

  const handleEditClick = (id: number) => {
    setBookId(id);
    // Der useEffect oben reagiert auf setBookId und lädt die Daten
  };

  // --- 3. FORM VIEW LOGIK ---

  const handleBackToList = () => {
    setBookId(null);
    setForm(emptyForm);
    loadBooks(); // Liste aktualisieren
    setView("list");
  };

  const addAutor = async () => {
    let autorToAdd: AutorDTO;

    if (selectedAutorId) {
      // Bestehenden Autor nutzen
      const found = availableAutoren.find((a) => a.id === selectedAutorId);
      if (!found) return alert("Autor nicht gefunden");
      autorToAdd = found;
    } else {
      // Neuen Autor erstellen
      if (!autor.vorname || !autor.nachname)
        return alert("Vorname und Nachname eingeben");
      autorToAdd = { ...autor, id: 0 };

      try {
        const createdAutor = await buchApi.createAutor(
          autorToAdd,
          bookId ?? undefined,
        );
        autorToAdd = createdAutor;
        setAvailableAutoren([...availableAutoren, createdAutor]);
      } catch (e) {
        console.error("Fehler beim Erstellen des Autors", e);
        return alert("Autor konnte nicht erstellt werden");
      }
    }

    const exists = form.autoren.some(
      (a) =>
        a.vorname.toLowerCase() === autorToAdd.vorname.toLowerCase() &&
        a.nachname.toLowerCase() === autorToAdd.nachname.toLowerCase(),
    );
    if (exists) return alert("Autor bereits hinzugefügt");

    setForm({ ...form, autoren: [...form.autoren, autorToAdd] });

    if (bookId) {
      try {
        await buchApi.updateBuchAutor(bookId, autorToAdd.id!);
      } catch (e) {
        console.error("Fehler beim Verknüpfen", e);
        alert("Autor konnte nicht verknüpft werden");
      }
    }

    setAutor({ vorname: "", nachname: "" });
    setSelectedAutorId(null);
  };

  const removeAutor = async (index: number) => {
    const autorToRemove = form.autoren[index];
    if (!autorToRemove?.id) {
      alert("Autor-ID fehlt");
      return;
    }

    try {
      if (bookId) {
        await buchApi.deleteAutorFromBuch(bookId, autorToRemove.id);
      }
      const newAutoren = form.autoren.filter((_, i) => i !== index);
      setForm({ ...form, autoren: newAutoren });
    } catch (e) {
      console.error("Fehler beim Entfernen", e);
      alert("Autor konnte nicht entfernt werden");
    }
  };

  const addExemplar = async () => {
    try {
      const savedExemplar = await buchApi.addExemplar(bookId!, exemplar);
      setForm({
        ...form,
        exemplare: [...form.exemplare, savedExemplar],
      });
      setExemplar({
        inventarnummer: "",
        status: "VERFUEGBAR",
        standort: "",
      });
    } catch (e) {
      console.error("Fehler beim Hinzufügen des Exemplars", e);
      alert("Exemplar konnte nicht hinzugefügt werden");
    }
  };

  const submitCreate = async () => {
    try {
      await buchApi.createBuch(form);
      alert("Buch erstellt!");
      handleBackToList();
    } catch (e) {
      alert("Fehler beim Erstellen");
    }
  };

  const submitUpdate = async () => {
    if (!bookId) return alert("Kein Buch geladen!");
    try {
      await buchApi.updateBuch(bookId, form);
      alert("Buch aktualisiert!");
      handleBackToList();
    } catch (e) {
      alert("Fehler beim Aktualisieren");
    }
  };

  // --- RENDER ---

  if (view === "list") {
    return (
      <div className="buch-container">
        <h2>Bücherübersicht</h2>
        <div className="list-controls">
          <input
            placeholder="Suchen nach Titel oder ISBN..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <button onClick={handleCreateNewClick} className="btn-primary">
            Neues Buch anlegen
          </button>
        </div>

        <table className="buch-table">
          <thead>
            <tr>
              <th style={{ width: "60px" }}>Cover</th>
              <th>Titel</th>
              <th>ISBN</th>
              <th>Verlag</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredBooks.map((b) => (
              <tr key={b.id || b.isbn}>
                <td>
                  {b.bildUrl ? (
                    <img
                      src={b.bildUrl}
                      alt="Cover"
                      style={{
                        width: "40px",
                        height: "55px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <div
                      style={{
                        width: "40px",
                        height: "55px",
                        background: "#eee",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                </td>
                <td>{b.titel}</td>
                <td>{b.isbn}</td>
                <td>{b.verlag}</td>
                <td>
                  <button onClick={() => handleEditClick(b.id!)}>
                    Bearbeiten
                  </button>
                </td>
              </tr>
            ))}
            {filteredBooks.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  Keine Bücher gefunden
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // VIEW === "form"
  return (
    <div className="buch-container">
      <div className="form-header">
        <button onClick={handleBackToList} style={{ backgroundColor: "#666" }}>
          ← Zurück zur Liste
        </button>
        <h2>{bookId ? "Buch bearbeiten" : "Neues Buch anlegen"}</h2>
      </div>

      <div className="form-section">
        <h3>Basisdaten</h3>
        <input
          value={form.titel}
          onChange={(e) => setForm({ ...form, titel: e.target.value })}
          placeholder="Titel"
        />
        <input
          value={form.isbn}
          onChange={(e) => setForm({ ...form, isbn: e.target.value })}
          placeholder="ISBN"
        />
        <input
          value={form.verlag}
          onChange={(e) => setForm({ ...form, verlag: e.target.value })}
          placeholder="Verlag"
        />
        <div style={{ display: "flex", gap: "10px" }}>
          <input
            type="number"
            value={form.jahr}
            onChange={(e) => setForm({ ...form, jahr: +e.target.value })}
            placeholder="Jahr"
          />
          <input
            value={form.sprache}
            onChange={(e) => setForm({ ...form, sprache: e.target.value })}
            placeholder="Sprache"
          />
        </div>
        <input
          value={form.medienart}
          onChange={(e) => setForm({ ...form, medienart: e.target.value })}
          placeholder="Medienart"
        />
        <input
          value={form.kategorieName}
          onChange={(e) => setForm({ ...form, kategorieName: e.target.value })}
          placeholder="Kategorie"
        />

        <div style={{ marginTop: "15px", marginBottom: "15px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
              fontSize: "0.9rem",
            }}
          >
            Buchcover URL (optional)
          </label>
          <div
            style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}
          >
            <input
              value={form.bildUrl || ""}
              onChange={(e) => setForm({ ...form, bildUrl: e.target.value })}
              placeholder="https://beispiel.de/bild.jpg"
              style={{ flex: 1 }}
            />
            <div
              style={{
                width: "60px",
                height: "90px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "#f9f9f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {form.bildUrl ? (
                <img
                  src={form.bildUrl}
                  alt="Vorschau"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              ) : (
                <span style={{ fontSize: "0.7rem", color: "#999" }}>
                  Kein Bild
                </span>
              )}
            </div>
          </div>
        </div>

        {!bookId && (
          <button onClick={submitCreate} className="btn-primary">
            Buch speichern
          </button>
        )}
        {bookId && (
          <button onClick={submitUpdate} className="btn-primary">
            Änderungen speichern
          </button>
        )}
      </div>

      <hr />

      {bookId && (
        <>
          <div className="form-section">
            <h3>Autoren (Optional)</h3>
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              Wähle einen bestehenden Autor aus oder erstelle einen neuen.
            </p>

            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              <input
                type="text"
                placeholder="Vorname"
                value={autor.vorname}
                onChange={(e) =>
                  setAutor({ ...autor, vorname: e.target.value })
                }
                style={{ flex: 1 }}
              />
              <input
                type="text"
                placeholder="Nachname"
                value={autor.nachname}
                onChange={(e) =>
                  setAutor({ ...autor, nachname: e.target.value })
                }
                style={{ flex: 1 }}
              />

              <div style={{ position: "relative", flex: 1 }}>
                <select
                  value={selectedAutorId ?? ""}
                  onChange={(e) =>
                    setSelectedAutorId(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "0.8rem 1rem",
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.4)",
                    background: "rgba(255,255,255,0.4)",
                  }}
                >
                  <option value="">Neuen Autor erstellen</option>
                  {availableAutoren
                    .filter(
                      (a) =>
                        a.vorname
                          .toLowerCase()
                          .includes(autor.vorname.toLowerCase()) &&
                        a.nachname
                          .toLowerCase()
                          .includes(autor.nachname.toLowerCase()) &&
                        !form.autoren.some((fa) => fa.id === a.id),
                    )
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.vorname} {a.nachname}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <button onClick={addAutor} className="btn-primary">
              Autor hinzufügen
            </button>

            <div className="autoren-list">
              {form.autoren.length === 0 && (
                <p style={{ fontSize: "0.85em", color: "#666" }}>
                  Keine Autoren hinzugefügt
                </p>
              )}
              {form.autoren.map((a, i) => (
                <div key={i} className="autoren-item">
                  <span>
                    {a.vorname} {a.nachname}
                  </span>
                  <button onClick={() => removeAutor(i)}>Entfernen</button>
                </div>
              ))}
            </div>
          </div>
          <hr />
          <div className="form-section">
            <h3>Exemplare</h3>
            <div style={{ display: "flex", gap: "10px" }}>
              <input
                value={exemplar.inventarnummer}
                onChange={(e) =>
                  setExemplar({
                    ...exemplar,
                    inventarnummer: e.target.value,
                  })
                }
                placeholder="Inventarnummer"
              />
              <input
                value={exemplar.standort}
                onChange={(e) =>
                  setExemplar({ ...exemplar, standort: e.target.value })
                }
                placeholder="Standort"
              />
            </div>
            <button onClick={addExemplar}>Exemplar hinzufügen</button>

            <ul>
              {form.exemplare.map((e, i) => (
                <li key={i}>
                  {e.inventarnummer} – {e.standort} ({e.status})
                </li>
              ))}
            </ul>
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              Exemplare können nur gelöscht werden, wenn ihr Status „Verfügbar“
              ist. Dies ist ausschließlich in der Buchsuche für Administratoren
              möglich.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default BuchVerwaltung;